import { useState, useEffect } from "react";
import {
  ShiftItem,
  ShiftItemV2,
  ShiftDayV2,
} from "../../../../interface/shift";
import { message, Tag } from "antd";
import { Button } from "../../../../components/ui/button";
import { useSelector, useDispatch } from "react-redux";
import api from "../../../../api/shift";
import { LoadingOutlined } from "@ant-design/icons";
import { setLoaded } from "../../../../app/commonplace";
import { format } from "date-fns";
import {
  Calendar,
  Clock,
  ChevronRight,
  Heart,
  ThumbsUp,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const ShiftsActive = (props: any) => {
  const commonplace = useSelector((state: any) => state.commonplace);
  const dispatch = useDispatch();

  const device = useSelector((state: any) => state.commonplace.device);
  const [loading, setLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  const toggleGroup = (shiftsId: string, e: any) => {
    e.stopPropagation();
    if (expandedGroups.includes(shiftsId)) {
      setExpandedGroups(expandedGroups.filter((id) => id !== shiftsId));
    } else {
      setExpandedGroups([...expandedGroups, shiftsId]);
    }
  };

  useEffect(() => {
    dispatch(
      setLoaded({
        loaded: true,
      })
    );
  }, []);
  const reAds = (e: any, shiftsId: string, shiftsDaysId: string) => {
    const ev = e || window.event;
    ev.stopPropagation();
    if (loading) return;
    setLoading(true);
    api.advertiseApi({
      params: { shiftsId, shiftsDaysId },
      callback: (res: any) => {
        setLoading(false);
        if (!res || !res.data) {
          return;
        }
        if (res.data.code === 200) {
          props.evtEmit("refresh");
        } else {
          message.error(res.data.message);
        }
      },
    });
  };

  const convertToShiftItem = (
    group: ShiftItemV2,
    day: ShiftDayV2
  ): ShiftItem => {
    const durationHours = (day.endTime - day.startTime) / 3600;
    const durationText =
      durationHours % 1 === 0
        ? `${durationHours} hr`
        : `${durationHours.toFixed(1)} hr`;

    return {
      date: (window as any).utils.formatDateWithWeek(
        format(new Date(day.startTime * 1000), "yyyy-MM-dd")
      ),
      time: `${(window as any).utils.fullToHalf(
        format(new Date(day.startTime * 1000), "HH:mm")
      )} - ${(window as any).utils.fullToHalf(
        format(new Date(day.endTime * 1000), "HH:mm")
      )}`,
      duration: durationText,
      applicants: day.applicants,
      includesNew: day.applicantsNew,
      group: group.shiftDays ? group.shiftDays.length : 1,
      id: day.shiftsDaysId,
      shiftsId: group.shiftsId,
      status: "normal",
      warnText: "",
      avatar: "",
      name: `${group.specialtyName} ${group.skillLevelName}`,
      totalNewApplicants: day.applicantsNew,
      specialty: group.specialty,
      specialtyName: group.specialtyName,
      skillLevel: group.skillLevel,
      skillLevelName: group.skillLevelName,
      favouriteApplicant: day.favouriteApplicant ? 1 : 0,
      recommendedApplicant: day.recommendedApplicant ? 1 : 0,
    };
  };

  const renderShiftItem = (
    item: ShiftItem,
    index: number,
    isChild: boolean = false
  ) => {
    // Animation for new applicants in expanded group
    const hasNewApplicants = item.includesNew > 0;
    // Static highlight instead of infinite pulse
    const highlightClass =
      isChild && hasNewApplicants
        ? "bg-green-50/80 border-green-200 shadow-sm"
        : "";
    // Slower entry animation for new items to draw attention
    const durationClass =
      isChild && hasNewApplicants ? "duration-1000" : "duration-300";

    return (
      <div
        onClick={() => {
          props.navigate(item.id, item.shiftsId);
        }}
        key={item.id}
        className={`bg-card/95 border border-border rounded-lg cursor-pointer hover:shadow-md hover:scale-[1.005] transition-all ${durationClass} ease-out animate-in fade-in slide-in-from-bottom-2 p-2 sm:p-3 md:py-2 md:px-1 ${
          isChild ? "ml-4 mt-2 border-l-4 border-l-primary/20" : ""
        } ${highlightClass}`}
        style={{
          animationDelay: `${index * 50}ms`,
          animationFillMode: "both",
        }}
      >
        {item.status === "error" || item.status === "normal" ? (
          <>
            {/* Mobile Layout - Stacked vertically */}
            <div className="relative block md:hidden">
              {/* Blue indicator on the left */}
              <div
                className={`absolute left-0 top-0 bottom-0 w-1 rounded-r ${
                  item.status === "error" ? "bg-destructive" : "bg-primary"
                }`}
              ></div>

              {/* First row - Date and Time with safe margins */}
              <div className="flex flex-col space-y-2 sm:space-y-3 pl-4 sm:pl-6 pr-3 sm:pr-4 mb-2 sm:mb-3 w-full">
                <div className="flex items-center">
                  <div className="text-[9px] sm:text-[10px] font-semibold text-muted-foreground/70 w-8 sm:w-10 flex-shrink-0">
                    Date
                  </div>
                  <div className="flex items-center text-[11px] sm:text-xs font-semibold text-foreground/80 flex-1">
                    <Calendar className="mr-1.5 sm:mr-2 text-muted-foreground w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    <span className="truncate">{item.date}</span>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="text-[9px] sm:text-[10px] font-semibold text-muted-foreground/70 w-8 sm:w-10 flex-shrink-0">
                    Time
                  </div>
                  <div className="flex items-center text-[11px] sm:text-xs font-semibold text-foreground/80 flex-1">
                    <Clock className="mr-1.5 sm:mr-2 text-muted-foreground w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    {item.time && !item.time.includes("NaN") ? (
                      <>
                        <span className="truncate">{item.time}</span>
                        <span className="text-muted-foreground/50 ml-1 sm:ml-2 text-[9px] sm:text-[10px] flex-shrink-0">
                          ({item.duration})
                        </span>
                      </>
                    ) : (
                      <span className="text-muted-foreground/50 text-[9px] sm:text-[10px]">
                        Time not available
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Second row - Applicants, Tags, Group */}
              <div className="flex items-start w-full pl-4 sm:pl-6 pr-3 sm:pr-4 gap-2">
                <div className="flex flex-col flex-1 gap-2">
                  {/* Row 1: Applicants & Group */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-medium text-muted-foreground">
                        Applicants:
                      </span>
                      <span className="text-xs font-bold text-foreground">
                        {item.applicants}
                      </span>
                      {item.includesNew > 0 && (
                        <span className="px-1.5 py-0.5 bg-green-50 text-green-700 border border-green-200 rounded text-[9px] font-medium">
                          {item.includesNew} New
                        </span>
                      )}
                    </div>

                    <div className="flex items-center bg-secondary/50 py-0.5 px-1.5 rounded">
                      <span className="text-[9px] font-medium text-muted-foreground mr-1">
                        Group
                      </span>
                      <span className="text-[10px] font-bold text-foreground">
                        {item.group === 1 ? "-" : item.group}
                      </span>
                    </div>
                  </div>

                  {/* Row 2: Tags */}
                  {(item.specialty || item.skillLevel) && (
                    <div className="flex flex-wrap gap-1.5">
                      {item.specialty && (
                        <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 border border-purple-200 rounded text-[9px] font-medium">
                          {item.specialtyName || item.specialty}
                        </span>
                      )}
                      {item.skillLevel && (
                        <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded text-[9px] font-medium">
                          {item.skillLevelName || item.skillLevel}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Row 3: Badges */}
                  {((item.favouriteApplicant ?? 0) > 0 ||
                    (item.recommendedApplicant ?? 0) > 0) && (
                    <div className="flex flex-wrap gap-1.5">
                      {(item.favouriteApplicant ?? 0) > 0 && (
                        <span className="px-1.5 py-0.5 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded text-[9px] font-medium flex items-center gap-1">
                          <span className="font-bold">
                            {item.favouriteApplicant}
                          </span>{" "}
                          Fav
                        </span>
                      )}
                      {(item.recommendedApplicant ?? 0) > 0 && (
                        <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded text-[9px] font-medium flex items-center gap-1">
                          <span className="font-bold">
                            {item.recommendedApplicant}
                          </span>{" "}
                          Rec
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <ChevronRight className="text-muted-foreground w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 mt-1" />
              </div>
            </div>

            {/* Desktop/Tablet Layout - Compact Single Row */}
            <div className="hidden md:flex items-center relative min-h-[56px]">
              {/* Blue indicator on the left */}
              <div
                className={`absolute left-0 top-0 bottom-0 w-1 rounded-r ${
                  item.status === "error" ? "bg-destructive" : "bg-primary"
                }`}
              ></div>

              {/* Date & Time */}
              <div className="flex items-center gap-4 pl-4 pr-4 border-r border-border/50 min-w-[280px]">
                <div className="flex items-center gap-1.5">
                  <Calendar className="text-muted-foreground w-3.5 h-3.5" />
                  <span className="text-sm font-semibold text-foreground">
                    {item.date}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="text-muted-foreground w-3.5 h-3.5" />
                  <span className="text-sm text-foreground/80">
                    {item.time}
                  </span>
                  <span className="text-[10px] text-muted-foreground bg-secondary/50 px-1.5 py-0.5 rounded">
                    {item.duration}
                  </span>
                </div>
              </div>

              {/* Role Tags */}
              <div className="flex items-center gap-2 px-4 border-r border-border/50 flex-1">
                <span className="px-2 py-0.5 bg-primary/10 text-primary border border-primary/20 rounded text-xs font-semibold">
                  {item.specialtyName || item.specialty || "General"}
                </span>
                {item.skillLevel && (
                  <span className="px-2 py-0.5 bg-secondary text-secondary-foreground border border-secondary-foreground/10 rounded text-xs">
                    {item.skillLevelName || item.skillLevel}
                  </span>
                )}
              </div>

              {/* Candidates Stats - Inline */}
              <div className="flex items-center gap-4 px-4">
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-muted-foreground">
                    Total:
                  </span>
                  <span className="text-base font-bold text-foreground">
                    {item.applicants}
                  </span>
                </div>
                {(item.totalNewApplicants || 0) > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-muted-foreground">
                      New:
                    </span>
                    <span className="text-base font-bold text-emerald-600">
                      {item.totalNewApplicants}
                    </span>
                  </div>
                )}

                {/* Badges */}
                {(item.favouriteApplicant ?? 0) > 0 && (
                  <span className="px-1.5 py-0.5 bg-rose-50 text-rose-600 border border-rose-200 rounded text-[10px] font-bold flex items-center gap-0.5">
                    <Heart className="w-2.5 h-2.5 fill-rose-600" />
                    {item.favouriteApplicant}
                  </span>
                )}
                {(item.recommendedApplicant ?? 0) > 0 && (
                  <span className="px-1.5 py-0.5 bg-sky-50 text-sky-700 border border-sky-200 rounded text-[10px] font-bold flex items-center gap-0.5">
                    <ThumbsUp className="w-2.5 h-2.5 fill-sky-700" />
                    {item.recommendedApplicant}
                  </span>
                )}
                {item.group > 1 && (
                  <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 border border-purple-200 rounded text-[10px] font-bold">
                    Group ({item.group})
                  </span>
                )}
              </div>
            </div>
          </>
        ) : (
          <div
            className={`bg-yellow-50/95 border border-yellow-200 rounded-lg relative hover:scale-[1.01] transition-all duration-300 ease-out p-2 sm:p-3 md:p-4`}
          >
            {/* Yellow indicator on the left */}
            <div className="absolute left-0 top-0 bottom-0 w-1 rounded-r bg-yellow-500"></div>

            <div
              className={`flex items-center justify-between flex-col space-y-2 sm:space-y-3 md:flex-row md:space-y-0`}
            >
              <div className="flex-1 w-full">
                <div
                  className={`bg-yellow-100 text-yellow-800 px-2 sm:px-3 py-1 rounded-full font-semibold inline-block text-[10px] sm:text-xs md:text-sm mb-2 sm:mb-3`}
                >
                  {item.warnText}
                </div>
                <div
                  className={`flex flex-col py-1 space-y-1 sm:space-y-2 pl-3 sm:pl-4 pr-3 sm:pr-4 md:pl-6 md:pr-8 md:border-r md:border-border`}
                >
                  <div className="flex items-center space-x-2">
                    <div
                      className={`font-semibold text-muted-foreground/70 flex-shrink-0 text-[9px] sm:text-[10px] md:text-xs w-6 sm:w-8 md:w-12`}
                    >
                      Date
                    </div>
                    <div
                      className={`flex items-center font-semibold text-foreground text-[11px] sm:text-xs md:text-sm`}
                    >
                      <Calendar
                        className={`mr-1.5 sm:mr-2 text-muted-foreground w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4`}
                      />
                      <span className="truncate">{item.date}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div
                      className={`font-semibold text-muted-foreground/70 flex-shrink-0 text-[9px] sm:text-[10px] md:text-xs w-6 sm:w-8 md:w-12`}
                    >
                      Time
                    </div>
                    <div
                      className={`flex items-center font-semibold text-foreground text-[11px] sm:text-xs md:text-sm`}
                    >
                      <Clock
                        className={`mr-1.5 sm:mr-2 text-muted-foreground w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4`}
                      />
                      {item.time && !item.time.includes("NaN") ? (
                        <>
                          <span className="truncate">{item.time}</span>
                          <span
                            className={`text-muted-foreground/50 ml-1 sm:ml-2 text-[9px] sm:text-[10px] md:text-xs`}
                          >
                            ({item.duration})
                          </span>
                        </>
                      ) : (
                        <span
                          className={`text-muted-foreground/50 text-[9px] sm:text-[10px] md:text-xs`}
                        >
                          Time not available
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className={`w-full md:w-auto md:ml-4 lg:ml-8`}>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full md:w-auto text-xs md:text-sm h-8 md:h-9"
                  onClick={(e) => {
                    reAds(e, item.shiftsId, item.id);
                  }}
                >
                  <span className="whitespace-nowrap">
                    Re-advertise {loading ? <LoadingOutlined /> : ""}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (group: ShiftItemV2, index: number) => {
    const isExpanded = expandedGroups.includes(group.shiftsId);

    return (
      <div
        key={group.shiftsId}
        className="mb-4 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
      >
        {/* Group Header */}
        <div
          className="p-4 bg-gray-50 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
          onClick={(e) => toggleGroup(group.shiftsId, e)}
        >
          <div className="flex items-center gap-4">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900">
                  {group.specialtyName || group.specialty}
                </span>
                {group.skillLevelName && (
                  <Tag className="m-0 text-xs">{group.skillLevelName}</Tag>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                From {group.earliestStartTimeString} To{" "}
                {group.latestEndTimeString || "Unknown"}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 text-sm">
              <div className="flex flex-col items-end">
                <span className="font-bold text-gray-900">
                  {group.shiftDays.length}
                </span>
                <span className="text-[10px] text-gray-500 uppercase">
                  Shifts
                </span>
              </div>
              <div className="w-px h-8 bg-gray-300 mx-1"></div>
              <div className="flex flex-col items-end">
                <span className="font-bold text-gray-900">
                  {group.applicantsCount}
                </span>
                <span className="text-[10px] text-gray-500 uppercase">
                  Applicants
                </span>
              </div>
              {group.newApplicantsCount > 0 && (
                <>
                  <div className="w-px h-8 bg-gray-300 mx-1"></div>
                  <div className="flex flex-col items-end">
                    <span className="font-bold text-green-600">
                      {group.newApplicantsCount}
                    </span>
                    <span className="text-[10px] text-green-600 uppercase">
                      New
                    </span>
                  </div>
                </>
              )}
            </div>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>

        {/* Group Content (Shift Days) */}
        {isExpanded && (
          <div className="p-2 bg-gray-50/50 border-t border-gray-200 space-y-2">
            {group.shiftDays.map((day, dayIndex) => {
              const shiftItem = convertToShiftItem(group, day);
              return renderShiftItem(shiftItem, dayIndex, true);
            })}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    // Auto-expand logic removed as per user request
  }, [props.list]);

  return (
    <div className="min-h-[200px] overflow-hidden">
      <div className="space-y-2 overflow-hidden">
        {props.list.map((item: ShiftItem | ShiftItemV2, index: number) => {
          // Check if it's a V2 Item
          if ("isGroup" in item) {
            if (item.isGroup) {
              return renderGroup(item as ShiftItemV2, index);
            } else {
              // V2 but flat (ungrouped) - render as single item
              if (item.shiftDays && item.shiftDays.length > 0) {
                return renderShiftItem(
                  convertToShiftItem(item as ShiftItemV2, item.shiftDays[0]),
                  index
                );
              }
              return null;
            }
          } else {
            // Fallback for V1 or flat items
            return renderShiftItem(item as ShiftItem, index);
          }
        })}
      </div>
    </div>
  );
};

export default ShiftsActive;
